import {
  getTranslationSafetyError,
  type TranslationSafetyErrorCode,
} from "@redotech/i18n-backend/translation-safety";
import * as z from "zod";
import { EmailBlockType, TableMode } from "./email-builder";
import type {
  EmailBlock,
  EmailTemplate,
  NonRecursiveEmailBlock,
} from "./email-template";
import type { EmailTemplateLocaleCopy } from "./email-template-translations";
import { getLinkTextError } from "./email-translation-link-text";
import { getLiquidStructureError } from "./email-translation-liquid-structure";

const tableCellPosition = {
  row: z.number().int().min(0),
  column: z.number().int().min(0),
};

/** Where a translatable string lives, so editors can label it. */
export const emailTranslationFieldSchema = z.discriminatedUnion("type", [
  z.object({ type: z.literal("subject") }),
  z.object({ type: z.literal("preview") }),
  z.object({ type: z.literal("text") }),
  z.object({ type: z.literal("html") }),
  z.object({ type: z.literal("button-text") }),
  z.object({ type: z.literal("tracking-button-text") }),
  z.object({ type: z.literal("header-text") }),
  z.object({ type: z.literal("header-alt-text") }),
  z.object({ type: z.literal("image-alt-text") }),
  z.object({ type: z.literal("image-caption") }),
  z.object({ type: z.literal("menu-item") }),
  z.object({ type: z.literal("review-request-header") }),
  z.object({ type: z.literal("checkout-button-text") }),
  z.object({ type: z.literal("product-button-text") }),
  z.object({ type: z.literal("gift-receipt-button-text") }),
  z.object({ type: z.literal("order-details-title") }),
  z.object({ type: z.literal("items-title") }),
  z.object({ type: z.literal("other-items-title") }),
  z.object({ type: z.literal("compare-at-price-label") }),
  z.object({ type: z.literal("subtotal-label") }),
  z.object({ type: z.literal("savings-label") }),
  z.object({ type: z.literal("scratch-fallback-text") }),
  z.object({ type: z.literal("review-title-placeholder") }),
  z.object({ type: z.literal("review-description-placeholder") }),
  z.object({ type: z.literal("review-submit-button-text") }),
  z.object({ type: z.literal("subscribe-button-text") }),
  z.object({ type: z.literal("subscribed-button-text") }),
  z.object({ type: z.literal("subscription-expired-button-text") }),
  z.object({ type: z.literal("subscription-title") }),
  z.object({ type: z.literal("table-header") }),
  z.object({ type: z.literal("table-cell"), ...tableCellPosition }),
  z.object({ type: z.literal("table-image-alt-text"), ...tableCellPosition }),
]);

export type EmailTranslationField = z.infer<typeof emailTranslationFieldSchema>;

export const emailTranslationEntrySchema = z.object({
  key: z.string(),
  text: z.string(),
  kind: z.enum(["text", "html"]),
  field: emailTranslationFieldSchema,
});

export type EmailTranslationEntry = z.infer<typeof emailTranslationEntrySchema>;

/** Translated text by entry key. Missing or blank keys keep the source text. */
export type EmailTranslationTexts = Readonly<
  Partial<Record<string, string | null>>
>;

type EmailTranslationContent = Pick<
  EmailTemplate,
  "subject" | "emailPreview" | "sections"
>;

/**
 * Called for every non-blank translatable string. Returning a string replaces
 * it; returning undefined keeps the source.
 */
type Visit = (entry: EmailTranslationEntry) => string | undefined;

function visitText(
  visit: Visit,
  key: string,
  source: string,
  kind: EmailTranslationEntry["kind"],
  field: EmailTranslationField,
): string;
function visitText(
  visit: Visit,
  key: string,
  source: string | undefined,
  kind: EmailTranslationEntry["kind"],
  field: EmailTranslationField,
): string | undefined;
function visitText(
  visit: Visit,
  key: string,
  source: string | null | undefined,
  kind: EmailTranslationEntry["kind"],
  field: EmailTranslationField,
): string | null | undefined;
function visitText(
  visit: Visit,
  key: string,
  source: string | null | undefined,
  kind: EmailTranslationEntry["kind"],
  field: EmailTranslationField,
): string | null | undefined {
  if (source == null || source.trim() === "") {
    return source;
  }
  return visit({ key, text: source, kind, field }) ?? source;
}

function blockKey(block: { blockId?: unknown }, suffix: string): string {
  return `block:${String(block.blockId)}:${suffix}`;
}

function visitTable(block: EmailBlock.Table, visit: Visit): EmailBlock.Table {
  if (block.mode !== TableMode.STATIC) {
    return block;
  }
  return {
    ...block,
    columns: block.columns.map((column) => ({
      ...column,
      headerLabel: visitText(
        visit,
        blockKey(block, `table:column:${column.id}:headerLabel`),
        column.headerLabel,
        "text",
        { type: "table-header" },
      ),
    })),
    staticRows: block.staticRows.map((row, rowIndex) =>
      row.map((cell, columnIndex) => {
        // Id-less cells have no stable key, so they stay in the source language.
        if (!cell.id) {
          return cell;
        }
        const cellKey = `table:cell:${cell.id}`;
        const position = { row: rowIndex, column: columnIndex };
        return cell.cellType === "text"
          ? {
              ...cell,
              content: visitText(
                visit,
                blockKey(block, `${cellKey}:content`),
                cell.content,
                "html",
                { type: "table-cell", ...position },
              ),
            }
          : {
              ...cell,
              altText: visitText(
                visit,
                blockKey(block, `${cellKey}:altText`),
                cell.altText,
                "text",
                { type: "table-image-alt-text", ...position },
              ),
            };
      }),
    ),
  };
}

/** Brand kit buttons, including V2, carry their label in `text`. */
function visitBrandKitButton<T extends { text: string }>(
  button: T,
  visit: Visit,
  key: string,
  field: EmailTranslationField,
): T {
  return { ...button, text: visitText(visit, key, button.text, "text", field) };
}

/** Buttons nested in another block carry their label in `buttonText`. */
function visitInlineButton<T extends { buttonText: string }>(
  button: T,
  visit: Visit,
  key: string,
  field: EmailTranslationField,
): T {
  return {
    ...button,
    buttonText: visitText(visit, key, button.buttonText, "text", field),
  };
}

// Each case spreads the block it was given, so the variant is preserved.
function visitLeafBlock<T extends NonRecursiveEmailBlock>(
  block: T,
  visit: Visit,
): T;
function visitLeafBlock(
  block: NonRecursiveEmailBlock,
  visit: Visit,
): NonRecursiveEmailBlock {
  if (block.blockId == null) {
    return block;
  }
  switch (block.type) {
    case EmailBlockType.TEXT:
      return {
        ...block,
        text: visitText(visit, blockKey(block, "text"), block.text, "html", {
          type: "text",
        }),
      };
    case EmailBlockType.HTML:
      return {
        ...block,
        html: visitText(visit, blockKey(block, "html"), block.html, "html", {
          type: "html",
        }),
      };
    case EmailBlockType.BUTTON:
      return {
        ...block,
        buttonText: visitText(
          visit,
          blockKey(block, "buttonText"),
          block.buttonText,
          "text",
          { type: "button-text" },
        ),
      };
    case EmailBlockType.TRACKING_INFO:
      return {
        ...block,
        buttonText: visitText(
          visit,
          blockKey(block, "buttonText"),
          block.buttonText,
          "text",
          { type: "tracking-button-text" },
        ),
      };
    case EmailBlockType.HEADER:
      return {
        ...block,
        text: visitText(visit, blockKey(block, "text"), block.text, "text", {
          type: "header-text",
        }),
        altText: visitText(
          visit,
          blockKey(block, "altText"),
          block.altText,
          "text",
          { type: "header-alt-text" },
        ),
      };
    case EmailBlockType.IMAGE:
      return {
        ...block,
        altText: visitText(
          visit,
          blockKey(block, "altText"),
          block.altText,
          "text",
          { type: "image-alt-text" },
        ),
        caption: visitText(
          visit,
          blockKey(block, "caption"),
          block.caption,
          "text",
          { type: "image-caption" },
        ),
      };
    case EmailBlockType.MENU:
      return {
        ...block,
        menuItems: block.menuItems.map((item) => ({
          ...item,
          label: visitText(
            visit,
            blockKey(block, `menu:${item.id}:label`),
            item.label,
            "text",
            { type: "menu-item" },
          ),
        })),
      };
    case EmailBlockType.TABLE:
      return visitTable(block, visit);
    case EmailBlockType.REVIEW_REQUEST:
      return {
        ...block,
        sectionHeader: visitText(
          visit,
          blockKey(block, "sectionHeader"),
          block.sectionHeader,
          "text",
          { type: "review-request-header" },
        ),
      };
    case EmailBlockType.TRACKABLE_SUMMARY:
      return {
        ...block,
        orderDetailsTitleLabel: visitText(
          visit,
          blockKey(block, "orderDetailsTitleLabel"),
          block.orderDetailsTitleLabel,
          "text",
          { type: "order-details-title" },
        ),
        itemsTitleLabel: visitText(
          visit,
          blockKey(block, "itemsTitleLabel"),
          block.itemsTitleLabel,
          "text",
          { type: "items-title" },
        ),
        otherItemsTitleLabel: visitText(
          visit,
          blockKey(block, "otherItemsTitleLabel"),
          block.otherItemsTitleLabel,
          "text",
          { type: "other-items-title" },
        ),
        compareAtPriceLabel: visitText(
          visit,
          blockKey(block, "compareAtPriceLabel"),
          block.compareAtPriceLabel,
          "text",
          { type: "compare-at-price-label" },
        ),
        subtotalLabel: visitText(
          visit,
          blockKey(block, "subtotalLabel"),
          block.subtotalLabel,
          "text",
          { type: "subtotal-label" },
        ),
        savingsLabel: visitText(
          visit,
          blockKey(block, "savingsLabel"),
          block.savingsLabel,
          "text",
          { type: "savings-label" },
        ),
        createGiftReceiptButton: visitBrandKitButton(
          block.createGiftReceiptButton,
          visit,
          blockKey(block, "createGiftReceiptButton:text"),
          { type: "gift-receipt-button-text" },
        ),
      };
    case EmailBlockType.PRODUCTS:
      return {
        ...block,
        checkoutButton: visitInlineButton(
          block.checkoutButton,
          visit,
          blockKey(block, "checkoutButton:buttonText"),
          { type: "checkout-button-text" },
        ),
        lineItemButtons: visitInlineButton(
          block.lineItemButtons,
          visit,
          blockKey(block, "lineItemButtons:buttonText"),
          { type: "product-button-text" },
        ),
      };
    case EmailBlockType.SHOPPABLE_PRODUCTS:
      return {
        ...block,
        checkoutButton: visitBrandKitButton(
          block.checkoutButton,
          visit,
          blockKey(block, "checkoutButton:text"),
          { type: "checkout-button-text" },
        ),
        productButtons: visitBrandKitButton(
          block.productButtons,
          visit,
          blockKey(block, "productButtons:text"),
          { type: "product-button-text" },
        ),
      };
    case EmailBlockType.ONE_CLICK_SUBSCRIPTION:
      return {
        ...block,
        buttons: {
          active: visitBrandKitButton(
            block.buttons.active,
            visit,
            blockKey(block, "buttons:active:text"),
            { type: "subscribe-button-text" },
          ),
          subscribed: visitBrandKitButton(
            block.buttons.subscribed,
            visit,
            blockKey(block, "buttons:subscribed:text"),
            { type: "subscribed-button-text" },
          ),
          expired: visitBrandKitButton(
            block.buttons.expired,
            visit,
            blockKey(block, "buttons:expired:text"),
            { type: "subscription-expired-button-text" },
          ),
        },
      };
    case EmailBlockType.SCRATCH_TO_REVEAL:
      return {
        ...block,
        fallbackText: visitText(
          visit,
          blockKey(block, "fallbackText"),
          block.fallbackText,
          "text",
          { type: "scratch-fallback-text" },
        ),
      };
    case EmailBlockType.SUBSCRIPTION_SKIP_OR_DELAY:
      // Only the initial heading reaches the shopper; the success screens and
      // the button label render from fixed copy.
      return {
        ...block,
        initialPage: {
          ...block.initialPage,
          title: visitText(
            visit,
            blockKey(block, "initialPage:title"),
            block.initialPage.title,
            "text",
            { type: "subscription-title" },
          ),
        },
      };
    // Nothing but styling, sizing, or block wiring to translate.
    case EmailBlockType.LINE:
    case EmailBlockType.SPACER:
    case EmailBlockType.CHROME_DINO:
    case EmailBlockType.SUBSCRIPTION_ADD_TO_ORDER:
    case EmailBlockType.SUBSCRIPTION_SWAP_PRODUCT:
      return block;
    // Their strings are store data or builder-only: the team address, link and
    // image URLs, a discount code, a saved section name.
    case EmailBlockType.FOOTER:
    case EmailBlockType.SOCIALS:
    case EmailBlockType.DISCOUNT:
    case EmailBlockType.QR_CODE:
    case EmailBlockType.SECTION_REFERENCE:
      return block;
    default:
      block satisfies never;
      return block;
  }
}

function visitInteractiveReviewRequest(
  block: EmailBlock.InteractiveReviewRequest,
  visit: Visit,
): EmailBlock.InteractiveReviewRequest {
  const withSuccessSection = {
    ...block,
    successSection: {
      ...block.successSection,
      blocks: block.successSection.blocks.map((child) =>
        visitLeafBlock(child, visit),
      ),
    },
  };
  if (block.blockId == null) {
    return withSuccessSection;
  }
  return {
    ...withSuccessSection,
    placeholderText: {
      title: visitText(
        visit,
        blockKey(block, "placeholderText:title"),
        block.placeholderText.title,
        "text",
        { type: "review-title-placeholder" },
      ),
      description: visitText(
        visit,
        blockKey(block, "placeholderText:description"),
        block.placeholderText.description,
        "text",
        { type: "review-description-placeholder" },
      ),
    },
    submitButton: visitBrandKitButton(
      block.submitButton,
      visit,
      blockKey(block, "submitButton:text"),
      { type: "review-submit-button-text" },
    ),
  };
}

// The trailing call narrows to NonRecursiveEmailBlock, so a new recursive block
// type stops compiling until it is handled here.
function visitBlock(block: EmailBlock, visit: Visit): EmailBlock {
  if (block.type === EmailBlockType.COLUMN) {
    return {
      ...block,
      columns: block.columns.map((child) =>
        child ? visitLeafBlock(child, visit) : child,
      ),
    };
  }
  if (block.type === EmailBlockType.INTERACTIVE_REVIEW_REQUEST) {
    return visitInteractiveReviewRequest(block, visit);
  }
  return visitLeafBlock(block, visit);
}

function visitBlocks(
  blocks: readonly EmailBlock[],
  visit: Visit,
): EmailBlock[] {
  return blocks.map((block) => visitBlock(block, visit));
}

function collector(): { entries: EmailTranslationEntry[]; visit: Visit } {
  const entries: EmailTranslationEntry[] = [];
  return {
    entries,
    visit: (entry) => {
      entries.push(entry);
      return undefined;
    },
  };
}

type EmailTranslationSafetyErrorCode =
  | TranslationSafetyErrorCode
  | NonNullable<ReturnType<typeof getLiquidStructureError>>
  | NonNullable<ReturnType<typeof getLinkTextError>>;

function getEmailTranslationSafetyError(
  source: string,
  translation: string,
): EmailTranslationSafetyErrorCode | undefined {
  return (
    getTranslationSafetyError(source, translation) ??
    getLiquidStructureError(source, translation) ??
    getLinkTextError(source, translation)
  );
}

function translator(texts: EmailTranslationTexts): Visit {
  return (entry) => {
    const translation = texts[entry.key];
    if (translation == null || translation.trim() === "") {
      return undefined;
    }
    return getEmailTranslationSafetyError(entry.text, translation)
      ? undefined
      : translation;
  };
}

/** Every non-blank merchant-authored string, in document order. */
export function getEmailTemplateTranslationEntries(
  content: EmailTranslationContent,
): EmailTranslationEntry[] {
  const { entries, visit } = collector();
  visitText(visit, "subject", content.subject, "text", { type: "subject" });
  visitText(visit, "preview", content.emailPreview, "text", {
    type: "preview",
  });
  visitBlocks(content.sections, visit);
  return entries;
}

export function getEmailBlocksTranslationEntries(
  blocks: readonly EmailBlock[],
): EmailTranslationEntry[] {
  const { entries, visit } = collector();
  visitBlocks(blocks, visit);
  return entries;
}

/**
 * Builds a full localized copy from the source. Blank, missing, or unsafe
 * translations (changed Liquid, HTML, placeholders, or link text) keep the
 * source text.
 */
export function applyEmailTemplateTranslations(
  content: EmailTranslationContent,
  texts: EmailTranslationTexts,
): EmailTemplateLocaleCopy<EmailBlock[]> {
  const visit = translator(texts);
  return {
    subject: visitText(visit, "subject", content.subject, "text", {
      type: "subject",
    }),
    emailPreview: visitText(visit, "preview", content.emailPreview, "text", {
      type: "preview",
    }),
    sections: visitBlocks(content.sections, visit),
  };
}

export function applyEmailBlocksTranslations(
  blocks: readonly EmailBlock[],
  texts: EmailTranslationTexts,
): EmailBlock[] {
  return visitBlocks(blocks, translator(texts));
}

/** Entry text by key, e.g. to read a stored locale copy back into an editor. */
export function emailTranslationTextsByKey(
  entries: readonly EmailTranslationEntry[],
): Record<string, string> {
  return Object.fromEntries(entries.map(({ key, text }) => [key, text]));
}

export interface UnsafeEmailTranslation {
  key: string;
  error: EmailTranslationSafetyErrorCode;
}

/** Non-blank translations that would be dropped by the apply functions. */
export function findUnsafeEmailTranslations(
  entries: readonly EmailTranslationEntry[],
  texts: EmailTranslationTexts,
): UnsafeEmailTranslation[] {
  const unsafe: UnsafeEmailTranslation[] = [];
  for (const entry of entries) {
    const translation = texts[entry.key];
    if (translation == null || translation.trim() === "") {
      continue;
    }
    const error = getEmailTranslationSafetyError(entry.text, translation);
    if (error) {
      unsafe.push({ key: entry.key, error });
    }
  }
  return unsafe;
}
